import { computed, DestroyRef, inject, Injectable, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialogRef } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '@app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { FileActionsService } from '@app/shared/services/actions/file-actions.service';
import { ActiveProjectService } from '@app/shared/services/active-project.service';
import { LocationDTO } from '@app/shared/services/dtos/location/location.dto';
import { FileDataService, FileUrlParams } from '@app/shared/services/file-data.service';
import { LocationsDataService } from '@app/shared/services/locations-data.service';
import { DialogService } from '@app/shared/services/shared/dialog.service';
import { FileContext } from '@app/shared/types/enums/file-enums';
import { FileInfo } from '@app/shared/types/models/files/file-info';
import { FileMetadata } from '@app/shared/types/models/files/file-metadata';
import { UploadData } from '@app/shared/types/models/files/upload-data.model';
import {
  ChangeLocationParentDialogData,
  DuplicateLocationsDialogData,
  Location
} from '@app/shared/types/models/location/location.model';
import { Project } from '@app/shared/types/models/project/project.model';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable } from 'rxjs';

/**
 * LocationsActionsService - Facade for shared location mutation operations.
 *
 * This service centralizes all user-triggered actions that modify location data.
 * It handles file operations (upload, delete, update) and carousel modal management.
 *
 * Usage: Used by location components to avoid output chaining and logic duplication.
 *
 * Architecture:
 * - {@link LocationsActionsService}: User actions, mutations, side effects (toasts, modals)
 * - {@link LocationsDataService}: Data access, queries, getters, store management
 * - {@link LocationsHttpService}: HTTP API calls
 */
@Injectable({ providedIn: 'root' })
export class LocationsActionsService {
  private readonly dialogService: DialogService = inject(DialogService);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly fileActionsService: FileActionsService = inject(FileActionsService);
  private readonly filesService: FileDataService = inject(FileDataService);
  private readonly activeProjectService: ActiveProjectService = inject(ActiveProjectService);
  private readonly locationsDataService: LocationsDataService = inject(LocationsDataService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly activeProject: Signal<Project> = this.activeProjectService.activeProject;
  private readonly activeProjectLocations: Signal<Location[]> = computed(() =>
    this.locationsDataService.getProjectLocations(this.activeProject()?.id)()
  );
  public openCarouselWithLocationFiles(location: Signal<Location>, files: Signal<FileInfo[]>, index: number): void {
    const filesValue = files();
    if (filesValue && filesValue.length > 0) {
      this.dialogService.openCarouselDialog({
        files: files,
        currentIndex: index,
        onFilesAdded: (addedFiles: File[]) => this.addFiles(location, addedFiles, filesValue.length),
        onFileDeleted: (file: FileInfo) => this.deleteFile(location, file),
        onDescriptionChanged: (event: { file: FileInfo; description: string }) => this.updateFileDescription(event)
      });
    }
  }

  public addFiles(location: Signal<Location>, filesToAdd: File[], currentFilesCount: number): void {
    if (filesToAdd.length === 0) return;
    const params: FileUrlParams = {
      context: FileContext.locationFile,
      projectId: location().projectId,
      locationId: location().id
    };

    filesToAdd.forEach(file => {
      const upload: UploadData = {
        file,
        description: '',
        fileTags: [],
        orderNo: currentFilesCount ?? 0
      };
      this.filesService.uploadFile(params, upload);
    });
  }

  public deleteFile(location: Signal<Location>, file: FileInfo): void {
    this.dialogService
      .openConfirmationDialog({
        title: this.translateService.instant('delete-file-modal-delete'),
        desc: this.translateService.instant('delete-file-modal-confirmation', {
          fileName: file.name
        })
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmed => {
        if (confirmed) {
          const params: FileUrlParams = {
            context: FileContext.locationFile,
            projectId: location().projectId,
            locationId: location().id
          };

          this.filesService.deleteFile(params, file);
        }
      });
  }

  public updateFileDescription(event: { file: FileInfo; description: string }): void {
    const metadata: FileMetadata = {
      description: event.description,
      fileTags: [],
      orderNo: event.file.orderNo
    };
    this.fileActionsService.updateFile(event.file.id, metadata).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  public reorderLocations(orderedIds: string[]): Observable<LocationDTO[]> {
    if (orderedIds.length === 0) {
      return EMPTY;
    }

    return this.locationsDataService.reorderLocations(this.activeProject().id, orderedIds);
  }

  public deleteLocations(locations: Location[]): Observable<string> {
    return this.locationsDataService.deleteLocations(this.activeProject().id, locations);
  }

  public updateLocation(
    locationId: string,
    locationName: string,
    locationOrder: number,
    parentLocationId: string | undefined
  ): Observable<LocationDTO> {
    return this.locationsDataService.updateLocation(
      this.activeProject().id,
      locationId,
      locationName,
      locationOrder,
      parentLocationId
    );
  }

  public getEditableProjectLocations(): Signal<Location[]> {
    return computed(() =>
      this.activeProjectLocations()
        .filter(location => !location.readOnly)
        .sort((a, b) => a.orderNo - b.orderNo)
    );
  }

  public getReadonlyProjectLocations(): Signal<Location[]> {
    return computed(() =>
      this.activeProjectLocations()
        .filter(location => location.readOnly)
        .sort((a, b) => a.orderNo - b.orderNo)
    );
  }

  public getLocationsByParent(parentId: string): Signal<Location[]> {
    return this.locationsDataService.getLocationsByParent(parentId);
  }

  public getProjectLocations(): Signal<Location[]> {
    return this.activeProjectLocations;
  }

  public openAddLocationDialog(parentLocation?: Location): void {
    this.dialogService.openAddProjectLocationDialog(this.activeProject().id, parentLocation);
  }

  public openAddMultipleLocationDialog(parentLocation?: Location): void {
    this.dialogService.openAddMultipleLocationDialog(this.activeProject().id, parentLocation);
  }

  public openEditLocationDialog(location: Location): void {
    this.dialogService.openEditProjectLocationDialog(location);
  }

  public openChangeLocationParentDialog(data: ChangeLocationParentDialogData): void {
    this.dialogService.openChangeProjectLocationParentDialog(data);
  }

  public openDuplicateLocationDialog(data: DuplicateLocationsDialogData): void {
    this.dialogService.openDuplicateProjectLocationDialog(data);
  }

  public openDeleteConfirmationDialog(location: Location): MatDialogRef<ConfirmationDialogComponent> {
    return this.dialogService.openConfirmationDialog({
      title: this.translateService.instant('project-locations.modals.delete-title'),
      desc: this.translateService.instant('project-locations.modals.delete-description', {
        name: location.name
      })
    });
  }

  public getNextAvailableOrderNo(parentId?: string): number {
    const existingLocations = parentId
      ? this.locationsDataService.getLocationsByParent(parentId)()
      : this.activeProjectLocations();

    if (!existingLocations || existingLocations.length === 0) {
      return 0;
    }

    const maxOrderNo = Math.max(...existingLocations.map(loc => loc.orderNo ?? 0));
    return maxOrderNo + 1;
  }

  public addMultipleLocations(
    mainName: string,
    amount: number,
    startNumbering: number,
    parentId?: string
  ): Observable<LocationDTO[]> {
    const startOrderNo = this.getNextAvailableOrderNo(parentId);
    const locations = Array.from({ length: amount }, (_, index) => ({
      name: `${mainName} ${startNumbering + index}`,
      orderNo: startOrderNo + index,
      parentLocationId: parentId
    }));

    return this.locationsDataService.addMultipleLocations(this.activeProject().id, locations, mainName);
  }
}
